import React, { useMemo, useState } from 'react';
import Header from '../components/Header';

import {useCurrentUserFromFirestore} from "@/utils/useCurrentUserFromFirestore.js";
import { Card, Statistic, Radio, Select } from "antd";
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
dayjs.extend(isBetween);

import {
    LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import {useAuth} from "@/utils/auth.jsx";
import useUserApps from "@/utils/useUserApps.js";

const DashBoardPage = () => {

    const {user} = useAuth();

    const { data: apps } = useUserApps(user);


    const [selectedAppId, setSelectedAppId] = useState('all'); // 'all' — все приложения
    const [filter, setFilter] = useState('all');



    // Отфильтруем данные в зависимости от выбранного фильтра
    const filteredData = useMemo(() => {
        if (!apps || apps.length === 0) return [];

        // Собираем stats из приложений (один большой массив с объектами { appId, date, clicks, sales })
        let allStats = [];

        apps.forEach(app => {
            if (!app.stats) return;
            Object.entries(app.stats).forEach(([date, values]) => {
                allStats.push({
                    appId: app.id,
                    appName: app.appName, // можно для отображения
                    date: dayjs(date),
                    clicks: values.clicks ?? 0,
                    sales: values.sales ?? 0,
                });
            });
        });

        // Фильтрация по приложению
        if (selectedAppId !== 'all') {
            allStats = allStats.filter(stat => stat.appId === selectedAppId);
        }

        // Фильтрация по дате
        const now = dayjs();
        const lastMonthStart = now.subtract(1, 'month').startOf('month').startOf('day');
        const lastMonthEnd = now.subtract(1, 'month').endOf('month').endOf('day');

        const filtered = allStats.filter(stat => {
            if (filter === 'yesterday') {
                return stat.date.isSame(now.subtract(1, 'day'), 'day');
            }
            if (filter === 'lastMonth') {
                return stat.date.isBetween(lastMonthStart, lastMonthEnd, null, '[]');
            }
            return true;
        });

        // Преобразуем для графика — сгруппируем по дате (без времени)
        const grouped = filtered.reduce((acc, stat) => {
            const day = stat.date.format('YYYY-MM-DD');
            if (!acc[day]) acc[day] = { date: day, clicks: 0, sales: 0 };
            acc[day].clicks += stat.clicks;
            acc[day].sales += stat.sales;
            return acc;
        }, {});

        return Object.values(grouped).sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix());

    }, [apps, filter, selectedAppId]);

    const totalClicks = useMemo(() => filteredData.reduce((sum, item) => sum + item.clicks, 0), [filteredData]);
    const totalSales = useMemo(() => filteredData.reduce((sum, item) => sum + item.sales, 0), [filteredData]);

    const selectedApp = useMemo(() => {
        if (selectedAppId === 'all') return null;
        return apps?.find(app => app.id === selectedAppId) || null;
    }, [selectedAppId, apps]);

    return (

        <div className={`dashboard-page`} style={{ width: '100%' }}>
            <div className={`wrapper`}>

                <div className={`page page-app`}>
                    <Header title={`Statistics`} />


            <Select value={selectedAppId} onChange={setSelectedAppId} style={{ width: 200, marginRight: 16 }}>
                <Select.Option value="all">All apps </Select.Option>
                {apps?.map(app => (
                    <Select.Option key={app.id} value={app.id}>{app.appName}</Select.Option>
                ))}
            </Select>

            <Select value={filter} onChange={setFilter} style={{ width: 150 }}>
                <Select.Option value="all">All time</Select.Option>
                <Select.Option value="yesterday">Yesterday</Select.Option>
                <Select.Option value="lastMonth">Last month</Select.Option>
            </Select>


            <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                <Card style={{ flex: 1 }}>
                    <Statistic title="Total Clicks" value={totalClicks} valueStyle={{ color: 'green' }} />
                </Card>
                <Card style={{ flex: 1 }}>
                    <Statistic title="Total Sales" value={totalSales} valueStyle={{ color: 'blue' }} />
                </Card>
                <Card style={{ flex: 1 }}>
                    <Statistic title="Account Health" value={selectedApp?.accountHealth} valueStyle={{ color: 'black' }} />
                </Card>
                <Card style={{ flex: 1 }}>
                    <Statistic title="Available" value={selectedApp?.available} valueStyle={{ color: 'black' }} />
                </Card>
                <Card style={{ flex: 1 }}>
                    <Statistic title="ROAS" value={selectedApp?.roas} valueStyle={{ color: 'black' }} />
                </Card>
            </div>


            <div style={{ width: '100%', height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={filteredData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="clicks" stroke="green" activeDot={{ r: 8 }} />
                        <Line type="monotone" dataKey="sales" stroke="blue" />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            </div>
            </div>
        </div>
    );
};

export default DashBoardPage;



